import { isNil, isObjectLike } from "@mixitone/util";
import { Model, ModelConstructor } from "./model";

type Key<T extends string = string, I extends string = string> = `${T}-${I}`;
type UnknownModel = Model<any>;

/**
 * A key value store of all models that have been instantiated. This is used to
 * lookup models by their primary key. The values are weak references.
 */
class ModelRegistry {
  static instance = new ModelRegistry();

  models = new Map<Key, WeakRef<UnknownModel>>();

  constructor() {}

  clear() {
    this.models.clear();
  }

  register<T extends UnknownModel>(model: T): T {
    if (!model.persisted) return model;

    const key = this.keyFor(model);

    if (this.models.has(key)) {
      // Copy over any collections or references that have been loaded
      const existing = this.models.get(key)?.deref() as T | undefined;

      if (existing) {
        model.copyCollectionsTo(existing);
        model.copyReferencesTo(existing);
        return existing;
      }
    }

    this.models.set(key, new WeakRef(model));

    return model;
  }

  unregister(model: UnknownModel) {
    if (isNil(model.id)) return;

    this.models.delete(this.keyFor(model));
  }

  has(model: UnknownModel): boolean;
  has(key: Key): boolean;
  has<M extends Model<any>>(model: ModelConstructor<M>, id: string): boolean;
  has(...args: any[]) {
    if (args.length === 1) {
      const key = args[0] instanceof Model ? this.keyFor(args[0]) : args[0];
      return isObjectLike(this.models.get(key)?.deref());
    } else {
      const [model, id] = args;
      const key = this.keyFor(model.tableName, id);
      return this.has(key);
    }
  }

  get<M extends Model<any>>(key: Key<M["tableName"]>): M | undefined;
  get<M extends Model<any>>(model: ModelConstructor<M>, id: string): M | undefined;
  get<M extends Model<any>>(...args: any[]): UnknownModel | undefined {
    if (args.length === 1) {
      const key = args[0] as Key;
      const model = this.models.get(key)?.deref();
      const expectedTable = key.split("-")[0];
      if (model?.tableName !== expectedTable) return undefined;
      return model as unknown as M;
    } else {
      const [model, id] = args;
      const key = this.keyFor(model.tableName, id);
      return this.get(key);
    }
  }

  keyFor(tableName: string, id: string): Key;
  keyFor(model: UnknownModel): Key;
  keyFor(...args: any[]): Key {
    if (args.length === 1) {
      const [model] = args as [UnknownModel];
      return `${model.tableName}-${model.id}`;
    } else {
      const [tableName, id] = args as [string, string];
      return `${tableName}-${id}`;
    }
  }
}

export { ModelRegistry };
const instance = ModelRegistry.instance;
export default instance;
